<template>
  <a-modal
    :visible="visible"
    :width="width"
    @cancel="handleCancel"
    @ok="handleOk"
    cancelText="取消"
    okText="确定"
    title="选择区域"
  >
    <a-input-search
      @search="onSearch"
      class="toolbox"
      placeholder="输入地址进行搜索"
      style="width: 350px"
      v-model="searchKeyword"
    />
    <!-- Search Results Panel -->
    <div class="search-results-panel" v-if="searchResultVisible">
      <a-button @click="closeSearchResultPanel" class="close-btn" icon="close" shape="circle" size="small" />
      <div class="search-results-content">
        <a-list :data-source="addressArr" item-layout="horizontal" size="small">
          <a-list-item @click="selectPoi(item)" slot="renderItem" slot-scope="item, index">
            <a-list-item-meta>
              <template slot="title">
                <a>{{ `${index + 1}. ${item.name}` }}</a>
              </template>
              <template slot="description">{{ item.address }}</template>
            </a-list-item-meta>
          </a-list-item>
        </a-list>
      </div>
    </div>
    <div :id="mapId" class="map-container"></div>
    <div class="toolbar">
      当前坐标: {{ point[0] }}, {{ point[1] }}
      <br />
      地址: {{ address }}
    </div>
  </a-modal>
</template>

<script>
  import coordTransform from '@/utils/coordTransform';

  export default {
    name: 'TMapPointPicker',
    props: {
      defaultCenter: {
        type: Array,
        default: () => [116.397799, 39.906459]
      },
      defaultZoom: {
        type: Number,
        default: 12
      }
    },
    data () {
      return {
        width: 1200,
        visible: false,
        center: [...this.defaultCenter],
        zoom: this.defaultZoom,
        point: [],
        map: null,
        marker: null,
        address: '',
        region: '',
        geocoder: null,
        mapId: `map-picker-${Date.now()}`,
        mapClickHandler: null,
        localSearch: null,
        searchResultVisible: false,
        addressArr: [],
        searchKeyword: ''
      }
    },
    methods: {
      // 打开弹窗
      open ({ point, address }) {
        // Reset search state every time the modal is opened
        this.searchKeyword = ''
        this.addressArr = []
        this.searchResultVisible = false

        if (point && address) {
          this.address = address
          this.point = point
          this.center = point
        } else {
          // If no initial point is provided, reset to default state
          this.address = ''
          this.point = []
          this.center = [...this.defaultCenter]
        }
        this.visible = true
        this.$nextTick(() => {
          this.initMap()
        })
      },

      // 初始化地图
      initMap () {
        if (!window.T || !window.T.Map) {
          console.error('天地图API未加载')
          return
        }

        // 初始化地图
        if (!this.map) {
          this.map = new window.T.Map(this.mapId, {
            projection: 'EPSG:4326' // 使用经纬度坐标
          })
        }

        // 设置地图中心点和缩放级别
        this.map.centerAndZoom(new window.T.LngLat(this.center[0], this.center[1]), this.zoom)

        // 添加地图控件
        // this.map.addControl(new window.T.Control.Zoom())
        // this.map.addControl(new window.T.Control.Scale())
        // this.map.addControl(new window.T.Control.OverviewMap())

        // 先移除旧的事件监听
        if (this.mapClickHandler) {
          this.map.removeEventListener('click', this.mapClickHandler)
        }

        // 添加地图点击事件
        this.mapClickHandler = (e) => {
          const lng = e.lnglat.getLng()
          const lat = e.lnglat.getLat()
          this.point = [lng, lat]
          this.addMarker()
          this.getAddress()
        }
        this.map.addEventListener('click', this.mapClickHandler)

        // 如果有点位数据，添加标记
        if (this.point.length > 0) {
          this.addMarker()
        }

        // 初始化地理编码服务
        this.geocoder = new window.T.Geocoder()
      },

      // 添加标记点
      addMarker () {
        // 清除现有标记
        if (this.marker) {
          this.map.removeOverLay(this.marker)
        }

        // 创建标记
        const icon = new window.T.Icon({
          iconUrl: 'https://api.tianditu.gov.cn/img/map/marker.png',
          iconSize: new window.T.Point(25, 35),
          iconAnchor: new window.T.Point(12, 35)
        })

        this.marker = new window.T.Marker(
          new window.T.LngLat(this.point[0], this.point[1]),
          { icon: icon }
        )

        // 添加标记到地图
        this.map.addOverLay(this.marker)

        // 移动地图中心到标记点
        this.map.panTo(new window.T.LngLat(this.point[0], this.point[1]))
      },

      // 地址解析
      getAddress () {
        if (!this.geocoder) return

        const point = new window.T.LngLat(this.point[0], this.point[1])
        this.geocoder.getLocation(point, (result) => {
          if (result) {
            this.address = result.formatted_address;
            const addressComponent = result.addressComponent;
            const tiandituAdcode = addressComponent.county_code;
            this.region = tiandituAdcode.substring(3); // 天地图逆地址解析的编码前面3位是国家编码，不需要
          }
        });
      },

      // 搜索地点
      onSearch (keyword) {
        if (!window.T || !keyword) return

        const localSearch = new window.T.LocalSearch(this.map, {
          onSearchComplete: (result) => {
            // 根据天地图版本，pois可能在result.pois或result.getPois()
            const pois = result.pois || (result.getPois ? result.getPois() : [])
            console.log('Search result:', pois)

            if (!pois || pois.length === 0) {
              this.addressArr = []
              this.searchResultVisible = false
              this.$message.info('没有搜索到相关地址')
            } else {
              this.addressArr = pois
              this.searchResultVisible = true
            }
          },
          onError: (error) => {
            console.error('地点搜索失败:', error)
            this.$message.error('地点搜索失败')
            this.searchResultVisible = false
          }
        })

        localSearch.search(keyword)
      },

      // 从搜索结果中选择一个点
      selectPoi (poi) {
        const lnglatArr = poi.lonlat.split(',')
        const lng = parseFloat(lnglatArr[0])
        const lat = parseFloat(lnglatArr[1])

        this.point = [lng, lat]

        // addMarker 会移动地图中心点并更新标记
        this.addMarker()
        // getAddress 会进行逆地址解析
        this.getAddress()

        // 隐藏搜索结果面板
        this.closeSearchResultPanel()
      },

      // 关闭搜索结果面板
      closeSearchResultPanel () {
        this.searchResultVisible = false
      },

      // 提交
      handleOk () {
        const [lng, lat] = coordTransform.wgs84ToGcj02(this.point[0], this.point[1]); // 转换坐标

        this.$emit('change', {
          coordinateGcj02: [lng, lat],
          coordinateWgs84: this.point,
          address: this.address,
          region: this.region
        })
        this.visible = false
      },

      // 取消
      handleCancel () {
        this.visible = false
      },

      // 清理资源
      cleanup () {
        // 清除地图事件
        if (this.mapClickHandler && this.map) {
          this.map.removeEventListener('click', this.mapClickHandler)
          this.mapClickHandler = null
        }

        // 移除标记
        if (this.marker && this.map) {
          try {
            this.map.removeOverLay(this.marker)
          } catch (e) {
            console.warn('移除标记点失败:', e)
          }
          this.marker = null
        }

        // 清除搜索实例
        this.localSearch = null
        this.searchResultVisible = false
        this.addressArr = []

        // 销毁地图
        if (this.map) {
          this.map = null
        }

        // 清除地理编码服务
        this.geocoder = null
      },
    },
    beforeDestroy () {
      this.cleanup()
    }
  }
</script>

<style lang="less" scoped>
  /deep/ .ant-modal {
    max-width: 100vw !important;
    min-height: 600px;
    padding-bottom: 0;
    .ant-modal-body {
      height: calc(80vh - 55px - 103px);
      min-height: 500px;
      max-height: none !important;
      overflow-y: auto;

      .map-container {
        padding: 0px;
        margin: 0px;
        width: 100%;
        height: calc(80vh - 55px - 203px);
      }

      .toolbar {
        position: absolute;
        bottom: 20px;
        left: 0;
        width: 100%;
        height: 100px;
        background-color: #fff;
        font-size: 20px;
        text-align: center;
        line-height: 50px;
      }
    }
  }

  .toolbox {
    float: right;
    position: absolute;
    z-index: 999;
    right: 24px;
    top: 70px;
  }

  .search-results-panel {
    position: absolute;
    top: 110px;
    right: 24px;
    width: 350px;
    max-height: 40vh;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
  }

  .search-results-content {
    max-height: calc(40vh - 16px);
    overflow-y: auto;
  }

  .search-results-panel .ant-list-item {
    cursor: pointer;
    padding: 8px 12px;
    transition: background-color 0.3s;
  }

  .search-results-panel .ant-list-item:hover {
    background-color: #f0f2f5;
  }

  .search-results-panel .close-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
  }
</style>
